import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, TrendingDown } from "lucide-react";

const cur = (c) => (c === "GBP" ? "£" : c === "USD" ? "$" : c === "EUR" ? "€" : "");

export default function NegotiationPanel({ order, role, onChanged }) {
    // draft prices: { [product_id]: string }
    const [draft, setDraft] = useState(
        Object.fromEntries((order.items || []).map((it) => [it.product_id, String(it.price)]))
    );
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState("");

    const neg = order.negotiation || { proposals: [], admin_accepted_at: null, client_accepted_at: null };
    const iAccepted = role === "admin" ? !!neg.admin_accepted_at : !!neg.client_accepted_at;
    const otherAccepted = role === "admin" ? !!neg.client_accepted_at : !!neg.admin_accepted_at;
    const finished = ["confirmed", "in_production", "shipped", "cancelled"].includes(order.status);

    const propose = async () => {
        const prices = {};
        let dirty = false;
        (order.items || []).forEach((it) => {
            const v = parseFloat(draft[it.product_id]);
            if (!isNaN(v) && v > 0) {
                if (Math.abs(v - it.price) > 0.005) dirty = true;
                prices[it.product_id] = v;
            }
        });
        if (!dirty && !note.trim()) {
            return toast.error("Change at least one price or add a note before proposing");
        }
        setBusy("propose");
        try {
            await api.post(`/orders/${order.id}/propose`, { prices, note });
            toast.success("Price proposal sent");
            setNote("");
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Propose failed");
        } finally { setBusy(""); }
    };

    const accept = async () => {
        setBusy("accept");
        try {
            const r = await api.post(`/orders/${order.id}/accept`);
            toast.success(r.data.status === "agreed" ? "Both sides agreed — awaiting confirmation" : "You accepted the current price");
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Accept failed");
        } finally { setBusy(""); }
    };

    const confirm = async () => {
        setBusy("confirm");
        try {
            await api.post(`/orders/${order.id}/confirm`);
            toast.success("Order confirmed — moving to production");
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Confirm failed");
        } finally { setBusy(""); }
    };

    return (
        <div className="border border-border bg-white" data-testid={`negotiation-${order.id}`}>
            <div className="px-4 py-3 border-b border-border bg-neutral-50 flex items-center gap-3">
                <TrendingDown className="h-4 w-4 text-neutral-600" strokeWidth={1.5}/>
                <div className="overline text-neutral-600">Price Negotiation</div>
                <div className="ml-auto flex items-center gap-2 text-[10px] uppercase tracking-widest">
                    <span className={`px-2 py-1 border ${neg.admin_accepted_at ? "bg-green-50 border-green-500 text-green-800" : "border-neutral-300 text-neutral-500"}`}>A V Corp {neg.admin_accepted_at ? "✓" : "…"}</span>
                    <span className={`px-2 py-1 border ${neg.client_accepted_at ? "bg-green-50 border-green-500 text-green-800" : "border-neutral-300 text-neutral-500"}`}>Client {neg.client_accepted_at ? "✓" : "…"}</span>
                </div>
            </div>

            {/* Per-item editable prices */}
            <div className="p-4 space-y-2">
                {(order.items || []).map((it) => (
                    <div key={it.product_id} className="grid grid-cols-12 gap-2 items-center text-sm font-body" data-testid={`neg-item-${it.product_id}`}>
                        <div className="col-span-6 truncate">
                            <b>{it.title}</b>
                            {it.sku_code && <span className="text-neutral-500 font-mono text-xs ml-2">· {it.sku_code}</span>}
                        </div>
                        <div className="col-span-2 text-neutral-500 tabular-nums">× {it.quantity} pcs</div>
                        <div className="col-span-3 flex items-center gap-1">
                            <span className="text-neutral-500">{cur(it.currency)}</span>
                            <input
                                type="number" step="0.01" min="0"
                                value={draft[it.product_id]}
                                disabled={finished}
                                onChange={(e) => setDraft({ ...draft, [it.product_id]: e.target.value })}
                                className="w-full border border-black h-9 px-2 tabular-nums text-right font-mono text-sm rounded-none disabled:bg-neutral-100"
                                data-testid={`neg-price-input-${it.product_id}`}
                            />
                            <span className="text-neutral-500 text-xs">/pc</span>
                        </div>
                        <div className="col-span-1 text-right font-mono tabular-nums text-neutral-700">
                            {cur(it.currency)}{((parseFloat(draft[it.product_id]) || 0) * it.quantity).toFixed(2)}
                        </div>
                    </div>
                ))}
                <div className="border-t border-border pt-2 flex items-center justify-between text-sm font-body">
                    <div className="overline text-neutral-500">Provisional total</div>
                    <div className="font-display text-xl tabular-nums">
                        {cur(order.currency)}
                        {(order.items || []).reduce((s, it) => s + (parseFloat(draft[it.product_id]) || 0) * it.quantity, 0).toFixed(2)}
                    </div>
                </div>
            </div>

            {!finished && (
                <div className="px-4 pb-4 space-y-3">
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Add a note with your counter-offer (optional)"
                        rows={2}
                        className="w-full border border-black text-sm font-body p-2 rounded-none focus:outline-none"
                        data-testid="neg-note"
                    />
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={propose}
                            disabled={!!busy}
                            className="inline-flex items-center gap-1 border border-black bg-white text-black hover:bg-black hover:text-white uppercase tracking-widest text-[10px] font-semibold px-3 h-9 disabled:opacity-40"
                            data-testid="neg-propose-btn"
                        >
                            {busy === "propose" ? "Sending…" : "Send Counter-offer"}
                        </button>
                        <button
                            type="button"
                            onClick={accept}
                            disabled={!!busy || iAccepted}
                            className="inline-flex items-center gap-1 bg-black text-white uppercase tracking-widest text-[10px] font-semibold px-3 h-9 disabled:opacity-40"
                            data-testid="neg-accept-btn"
                        >
                            <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5}/>
                            {iAccepted ? "You Accepted ✓" : busy === "accept" ? "Accepting…" : "Accept Current Price"}
                        </button>
                        {role === "admin" && order.status === "agreed" && (
                            <button
                                type="button"
                                onClick={confirm}
                                disabled={!!busy}
                                className="inline-flex items-center gap-1 bg-green-700 text-white uppercase tracking-widest text-[10px] font-semibold px-3 h-9 disabled:opacity-40"
                                data-testid="neg-confirm-btn"
                            >
                                {busy === "confirm" ? "Confirming…" : "Confirm Order (lock price)"}
                            </button>
                        )}
                    </div>
                    {iAccepted && !otherAccepted && (
                        <div className="text-[11px] text-neutral-600 font-body">Waiting for the other side to accept. If they propose a new price your acceptance will reset.</div>
                    )}
                </div>
            )}

            {/* Proposals timeline */}
            {(neg.proposals || []).length > 0 && (
                <div className="border-t border-border p-4">
                    <div className="overline text-neutral-500 mb-2">Proposals</div>
                    <div className="space-y-2">
                        {(neg.proposals || []).slice().reverse().map((p, i) => (
                            <div key={i} className="text-xs font-body border-l-2 border-neutral-300 pl-3">
                                <div className="uppercase tracking-widest text-[10px] text-neutral-500">
                                    {new Date(p.at).toLocaleString()} · <b>{p.by_name}</b> ({p.by_role}) → total {cur(order.currency)}{p.total_after?.toFixed(2)}
                                </div>
                                {p.note && <div className="mt-1 text-neutral-700">{p.note}</div>}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
